import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Building2,
  GraduationCap,
  ArrowUpRight,
  ArrowRight,
  Atom,
  Microscope,
  Cpu,
  FlaskConical,
  Lightbulb,
  Sparkles,
  ChevronRight,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

export default function Science({
  accent = "#115e32",
  pageTitle = "School of Science and Engineering | University of Creative Technology Chittagong",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const dLinks = [
    {
      label: "Department of Computer Science and Engineering",
      short: "CSE",
      link: "/department/cse",
      icon: Cpu,
      description: "Computing, software, AI and digital innovation",
    },
    {
      label: "Department of Mechanical Engineering",
      short: "ME",
      link: "/department/me",
      icon: Building2,
      description: "Design, manufacturing and mechanical systems",
    },
    {
      label: "Department of Civil Engineering",
      short: "CE",
      link: "/department/ce",
      icon: Building2,
      description: "Infrastructure, construction and sustainable design",
    },
    {
      label: "Department of Electrical and Electronic Engineering",
      short: "EEE",
      link: "/department/eee",
      icon: Lightbulb,
      description: "Electrical power, electronics and smart technologies",
    },
    {
      label: "Department of Textile Engineering",
      short: "DTE",
      link: "/department/dte",
      icon: FlaskConical,
      description: "Textile science, materials and industrial innovation",
    },
  ];

  const pLinks = [
    { label: "B.Sc. in CSE", link: "/programs/cse", code: "CSE" },
    {
      label: "B.Sc. in Mechanical Engineering",
      link: "/programs/mechanical",
      code: "ME",
    },
    {
      label: "B.Sc. in Civil Engineering",
      link: "/programs/civil",
      code: "CE",
    },
    { label: "B.Sc. in EEE", link: "/programs/eee", code: "EEE" },
    { label: "B.Sc. in BTE", link: "/programs/bte", code: "BTE" },
  ];

  return (
    <main
      style={{ "--accent": accent }}
      className="overflow-hidden bg-[#f5fbfd] text-slate-800"
    >
      {/* ================= HERO SECTION ================= */}
      <section className="relative isolate min-h-[680px] overflow-hidden bg-[#013b52]">
        {/* Background gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#013449] via-[#014864] to-[#087d86]" />

        {/* Animated background shapes */}
        <motion.div
          className="absolute -right-32 -top-32 h-[450px] w-[450px] rounded-full border border-white/10"
          animate={{ rotate: 360 }}
          transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute left-12 top-12 h-[330px] w-[330px] rounded-full border border-white/10" />
        </motion.div>

        <motion.div
          className="absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-cyan-400/10 blur-3xl"
          animate={{ x: [0, 45, 0], y: [0, -25, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 md:px-12 lg:grid-cols-2">
          {/* Left hero content */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10"
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 p-2 shadow-xl backdrop-blur-md sm:h-20 sm:w-20">
                <img
                  src="/img/logo.png"
                  alt="UCTC logo"
                  className="h-full w-full object-contain"
                />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-200 sm:text-sm">
                  University of
                </p>
                <p className="mt-1 max-w-[260px] text-sm font-bold leading-5 text-white sm:text-base">
                  Creative Technology Chittagong
                </p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-cyan-100 backdrop-blur"
            >
              <Sparkles size={15} />
              Learn · Discover · Innovate
            </motion.div>

            <h1 className="text-4xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              School of
              <span className="block text-cyan-200">
                Science & Engineering
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base leading-8 text-cyan-50/80 sm:text-lg">
              Empowering future innovators, engineers, and technology leaders
              through quality education, creative thinking, and practical
              experience.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <motion.a
                href="/apply-online"
                whileHover={{ scale: 1.04, y: -3 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 px-6 py-3.5 font-bold text-[#123d4c] shadow-lg shadow-amber-900/20 transition"
              >
                Apply Now
                <ArrowUpRight size={18} />
              </motion.a>

              <motion.a
                href="#about"
                whileHover={{ scale: 1.04, y: -3 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:bg-white/20"
              >
                Explore the School
                <ArrowRight size={18} />
              </motion.a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 border-t border-white/15 pt-7">
              <div>
                <p className="text-2xl font-black text-white">5</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-cyan-100/70">
                  Departments
                </p>
              </div>
              <div className="h-12 w-px bg-white/15" />
              <div>
                <p className="text-2xl font-black text-white">5</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-cyan-100/70">
                  Programs
                </p>
              </div>
              <div className="h-12 w-px bg-white/15" />
              <div>
                <p className="text-2xl font-black text-white">Future</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wider text-cyan-100/70">
                  Focused learning
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right animated science illustration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative mx-auto flex w-full max-w-[560px] items-center justify-center"
          >
            <div className="absolute h-[350px] w-[350px] rounded-full bg-cyan-300/10 blur-3xl sm:h-[450px] sm:w-[450px]" />

            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
              className="absolute h-[330px] w-[330px] rounded-full border border-dashed border-cyan-100/30 sm:h-[440px] sm:w-[440px]"
            />

            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
              className="absolute h-[260px] w-[260px] rounded-full border border-white/20 sm:h-[350px] sm:w-[350px]"
            />

            <div className="relative flex h-[320px] w-[320px] items-center justify-center rounded-full border border-white/10 bg-white/[0.04] shadow-2xl backdrop-blur-sm sm:h-[420px] sm:w-[420px]">
              <motion.div
                animate={{ y: [0, -12, 0], rotate: [0, 4, 0] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative flex h-44 w-44 items-center justify-center rounded-[2.5rem] border border-white/20 bg-gradient-to-br from-white/20 to-white/5 shadow-2xl backdrop-blur-xl sm:h-56 sm:w-56"
              >
                <Atom
                  size={105}
                  strokeWidth={1.1}
                  className="text-cyan-100 sm:h-32 sm:w-32"
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-amber-300 shadow-lg shadow-amber-300/50"
                />
              </motion.div>

              {/* Floating icon cards */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4.5, repeat: Infinity }}
                className="absolute left-0 top-10 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-cyan-100 shadow-xl backdrop-blur-xl sm:left-3 sm:h-20 sm:w-20"
              >
                <Microscope size={32} />
              </motion.div>

              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
                className="absolute bottom-8 right-0 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-amber-200 shadow-xl backdrop-blur-xl sm:right-3 sm:h-20 sm:w-20"
              >
                <Cpu size={32} />
              </motion.div>

              <motion.div
                animate={{ x: [0, 8, 0], y: [0, -5, 0] }}
                transition={{ duration: 5.5, repeat: Infinity }}
                className="absolute bottom-12 left-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-emerald-200 shadow-xl backdrop-blur-xl"
              >
                <FlaskConical size={27} />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="absolute bottom-0 right-0 rounded-2xl border border-white/20 bg-white/10 px-5 py-4 shadow-xl backdrop-blur-xl sm:bottom-4"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-cyan-100">
                Our spirit
              </p>
              <p className="mt-1 text-sm font-bold text-white">
                Innovation · Creativity · Change
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 100"
            xmlns="http://www.w3.org/2000/svg"
            className="h-14 w-full md:h-20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,45 C240,100 480,5 720,50 C960,95 1200,10 1440,45 L1440,100 L0,100 Z"
              fill="#f5fbfd"
            />
          </svg>
        </div>
      </section>

      {/* ================= ABOUT SECTION ================= */}
      <section
        id="about"
        className="relative px-6 py-20 md:px-12 md:py-28"
      >
        <div className="mx-auto max-w-7xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="grid items-center gap-12 lg:grid-cols-2"
          >
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-cyan-100 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#014864]">
                <BookOpen size={15} />
                About the School
              </div>

              <h2 className="text-3xl font-black leading-tight text-[#014864] sm:text-4xl md:text-5xl">
                Where ideas become
                <span className="block text-[#0b8a9a]">real-world impact</span>
              </h2>

              <div className="mt-6 h-1.5 w-24 rounded-full bg-gradient-to-r from-cyan-500 to-amber-400" />

              <p className="mt-7 text-justify text-base leading-8 text-slate-600 sm:text-lg">
                The School of Science and Engineering (FSE) is a vibrant
                academic community dedicated to education, research, and
                innovation. Our departments prepare students to address
                real-world challenges through scientific knowledge, technical
                skills, and creative problem-solving.
              </p>

              <p className="mt-4 text-justify text-base leading-8 text-slate-600 sm:text-lg">
                Through practical learning and a commitment to continuous
                improvement, the School encourages students to explore new
                ideas, develop professional skills, and contribute to a
                technology-driven future.
              </p>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-cyan-200/50 to-emerald-100/50 blur-xl" />

              <div className="relative overflow-hidden rounded-[2rem] border border-white bg-white p-6 shadow-2xl shadow-cyan-900/10 sm:p-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      icon: Atom,
                      title: "Scientific Thinking",
                      text: "Question, explore, and discover",
                    },
                    {
                      icon: Cpu,
                      title: "Technology",
                      text: "Build skills for a digital world",
                    },
                    {
                      icon: Lightbulb,
                      title: "Innovation",
                      text: "Transform ideas into solutions",
                    },
                    {
                      icon: Microscope,
                      title: "Research",
                      text: "Learn through investigation",
                    },
                  ].map((item, index) => {
                    const Icon = item.icon;

                    return (
                      <motion.div
                        key={item.title}
                        whileHover={{ y: -6, scale: 1.02 }}
                        transition={{ duration: 0.25 }}
                        className={`rounded-2xl p-5 ${
                          index % 2 === 0
                            ? "bg-cyan-50"
                            : "bg-emerald-50"
                        }`}
                      >
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white text-[#087d86] shadow-sm">
                          <Icon size={24} />
                        </div>
                        <h3 className="font-bold text-[#014864]">
                          {item.title}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-slate-500">
                          {item.text}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= DEPARTMENTS SECTION ================= */}
      <section className="relative bg-white px-6 py-20 md:px-12 md:py-24">
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-cyan-50/80 to-transparent" />

        <div className="relative mx-auto max-w-7xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-100 text-[#014864]">
              <Building2 size={27} />
            </div>

            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0b8a9a]">
              Explore our academic units
            </p>

            <h2 className="mt-3 text-3xl font-black text-[#014864] sm:text-4xl">
              Our Departments
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-500">
              Discover the academic departments shaping tomorrow’s engineers
              and technology professionals.
            </p>

            <div className="mx-auto mt-6 h-1.5 w-20 rounded-full bg-gradient-to-r from-cyan-500 to-amber-400" />
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
          >
            {dLinks.map((dept, index) => {
              const Icon = dept.icon;

              return (
                <motion.a
                  key={dept.short}
                  href={dept.link}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition duration-300 hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-900/10"
                >
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-50 transition duration-500 group-hover:scale-150" />

                  <div className="relative flex items-start justify-between">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-100 to-emerald-50 text-[#087d86] transition duration-300 group-hover:scale-110 group-hover:from-[#014864] group-hover:to-[#087d86] group-hover:text-white">
                      <Icon size={27} />
                    </div>

                    <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-bold tracking-wider text-slate-400 transition group-hover:bg-cyan-50 group-hover:text-[#014864]">
                      {dept.short}
                    </span>
                  </div>

                  <h3 className="relative mt-6 text-lg font-extrabold leading-6 text-[#014864] transition group-hover:text-[#087d86]">
                    {dept.label}
                  </h3>

                  <p className="relative mt-3 text-sm leading-6 text-slate-500">
                    {dept.description}
                  </p>

                  <div className="relative mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="text-sm font-bold text-[#087d86]">
                      Explore Department
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-50 text-[#014864] transition duration-300 group-hover:translate-x-1 group-hover:bg-[#014864] group-hover:text-white">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= PROGRAMS SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#e8f9fc] via-[#f5fbfd] to-[#e8f7f1] px-6 py-20 md:px-12 md:py-24">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full border border-cyan-200/50"
        />

        <div className="relative mx-auto max-w-7xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#087d86] shadow-md shadow-cyan-900/5">
              <GraduationCap size={29} />
            </div>

            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#0b8a9a]">
              Build your future
            </p>

            <h2 className="mt-3 text-3xl font-black text-[#014864] sm:text-4xl">
              Offered Programs
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-500">
              Explore undergraduate programs designed to develop your
              knowledge, technical skills, and professional confidence.
            </p>

            <div className="mx-auto mt-6 h-1.5 w-20 rounded-full bg-gradient-to-r from-cyan-500 to-amber-400" />
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {pLinks.map((program, index) => (
              <motion.a
                key={program.code}
                href={program.link}
                variants={fadeUp}
                whileHover={{ y: -7, scale: 1.01 }}
                transition={{ duration: 0.25 }}
                className="group flex items-center gap-4 rounded-2xl border border-white bg-white/90 p-5 shadow-sm backdrop-blur transition duration-300 hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-900/10"
              >
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#014864] to-[#087d86] text-sm font-black text-white shadow-lg shadow-cyan-900/10 transition group-hover:rotate-3 group-hover:scale-105">
                  {program.code}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Undergraduate
                  </p>
                  <h3 className="mt-1 font-bold leading-5 text-[#014864] transition group-hover:text-[#087d86]">
                    {program.label}
                  </h3>
                </div>

                <ChevronRight
                  size={20}
                  className="shrink-0 text-slate-300 transition group-hover:translate-x-1 group-hover:text-[#087d86]"
                />
              </motion.a>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-14 overflow-hidden rounded-3xl bg-gradient-to-r from-[#013b52] via-[#014864] to-[#087d86] px-6 py-10 text-center shadow-xl shadow-cyan-900/10 sm:px-10"
          >
            <div className="mx-auto max-w-3xl">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-amber-300">
                <Sparkles size={24} />
              </div>

              <h3 className="mt-5 text-2xl font-black text-white sm:text-3xl">
                Ready to shape your future?
              </h3>

              <p className="mx-auto mt-3 max-w-xl leading-7 text-cyan-50/80">
                Take the next step toward a future in science, engineering,
                and technology.
              </p>

              <motion.a
                href="/apply-online"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                className="mt-7 inline-flex items-center gap-2 rounded-xl bg-amber-400 px-6 py-3.5 font-bold text-[#123d4c] shadow-lg transition hover:bg-amber-300"
              >
                Start Your Application
                <ArrowRight size={18} />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}